class HeroPrimary {
    constructor(section) {
        this.section = section;
        this.slides = Array.from(this.section.querySelectorAll('.hero-primary__slide'));
        this.navButtons = Array.from(this.section.querySelectorAll('.hero-primary__nav-button'));
        this.progressFills = Array.from(this.section.querySelectorAll('.hero-primary__nav-progress-fill'));
        this.activeIndex = 0;
        this.autoplayDelay = 5000;
        this.timeoutId = null;
        this.visibilityHandler = null;

        if (!this.slides.length) {
            return;
        }

        this.section.style.setProperty('--hero-primary-duration', `${this.autoplayDelay}ms`);

        this.init();
    }

    init() {
        this.bindEvents();
        this.setActiveSlide(0, false);

        if (this.slides.length > 1) {
            requestAnimationFrame(() => {
                this.startCycle();
            });

            this.setupVisibilityHandling();
        }
    }

    bindEvents() {
        if (!this.navButtons.length) {
            return;
        }

        this.navButtons.forEach((button, index) => {
            button.addEventListener('click', () => {
                if (index === this.activeIndex) {
                    return;
                }

                this.goToSlide(index);
            });
        });
    }

    setActiveSlide(index, resetCycle = true) {
        this.slides.forEach((slide, slideIndex) => {
            const isActive = slideIndex === index;

            slide.classList.toggle('is-active', isActive);
            slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        this.navButtons.forEach((button, buttonIndex) => {
            const isActive = buttonIndex === index;

            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.activeIndex = index;

        if (this.slides.length < 2) {
            return;
        }

        this.resetProgress();
        this.resetZoom();

        if (resetCycle) {
            this.startCycle();
        }
    }

    goToSlide(index) {
        this.stopCycle();
        this.setActiveSlide(index);
    }

    goToNextSlide() {
        const nextIndex = (this.activeIndex + 1) % this.slides.length;
        this.setActiveSlide(nextIndex);
    }

    startCycle() {
        this.stopCycle();
        this.startProgress();
        this.startZoom();

        this.timeoutId = window.setTimeout(() => {
            this.goToNextSlide();
        }, this.autoplayDelay);
    }

    stopCycle() {
        if (this.timeoutId) {
            window.clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    resetProgress() {
        this.progressFills.forEach((fill) => {
            fill.style.transition = 'none';
            fill.style.transform = 'scaleX(0)';
        });
    }

    startProgress() {
        const activeFill = this.progressFills[this.activeIndex];

        if (!activeFill) {
            return;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                activeFill.style.transition = `transform ${this.autoplayDelay}ms linear`;
                activeFill.style.transform = 'scaleX(1)';
            });
        });
    }

    resetZoom() {
        this.slides.forEach((slide) => {
            const image = slide.querySelector('.hero-primary__bg-image');

            if (!image) {
                return;
            }

            image.style.transition = 'none';
            image.style.transform = 'scale(1)';
        });
    }

    startZoom() {
        const activeSlide = this.slides[this.activeIndex];

        if (!activeSlide) {
            return;
        }

        const image = activeSlide.querySelector('.hero-primary__bg-image');

        if (!image) {
            return;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                image.style.transition = `transform ${this.autoplayDelay}ms linear`;
                image.style.transform = 'scale(1.08)';
            });
        });
    }

    setupVisibilityHandling() {
        this.visibilityHandler = () => {
            if (document.hidden) {
                this.stopCycle();
                this.resetProgress();
                this.resetZoom();
            } else {
                this.startCycle();
            }
        };

        document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    destroy() {
        this.stopCycle();

        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
        }

        this.resetProgress();
        this.resetZoom();
    }

    static initAll() {
        const sections = document.querySelectorAll('.js-hero-primary');

        if (!sections.length) {
            return;
        }

        sections.forEach((section) => {
            if (section.heroPrimaryInstance) {
                return;
            }

            section.heroPrimaryInstance = new HeroPrimary(section);
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    HeroPrimary.initAll();
});